// src/pages/DueDiligencePortal.js

import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./css/duediligence.css";

import { makeCompanyNav } from "./nav";
import Main from "./Main";
import useKoncernMembers from "../hooks/useKoncernMembers";
import useKoncernScope from "../hooks/useKoncernScope";
import useWorkspaceLifecycle from "../hooks/useWorkspaceLifecycle";
import KoncernScopePicker from "./components/KoncernScopePicker";
import {
  WorkspaceLockOverlay,
  WorkspaceCloseBanner,
  CloseWorkspaceButton,
} from "./components/WorkspaceLifecycleUI";

import RiskProfileQuestionnaireForm from "./components/RiskProfileQuestionnaireForm";
import OwnerQuestionnaireForm from "./components/OwnerQuestionnaireForm";
import DueDiligenceChecklist from "./components/DueDiligenceChecklist";

import { DDQ_SECTIONS } from "../config/questionSchemas";
import { API_BASE, getCsrfToken } from "../services/server";

function joinUrl(base, path) {
  if (!path.startsWith("/")) path = `/${path}`;
  const b = (base || "").replace(/\/+$/, "");
  return `${b}${path}`;
}

async function apiFetch(path, { method = "GET", body, signal } = {}) {
  const url = joinUrl(API_BASE, path);
  const csrf = getCsrfToken();

  const headers = { Accept: "application/json" };
  const hasBody = body !== undefined && body !== null;

  if (hasBody) headers["Content-Type"] = "application/json";

  const unsafe = !["GET", "HEAD", "OPTIONS", "TRACE"].includes(method.toUpperCase());
  if (unsafe && csrf) headers["X-CSRFToken"] = csrf;

  const res = await fetch(url, {
    method,
    credentials: "include",
    headers,
    signal,
    body: hasBody ? JSON.stringify(body) : undefined,
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => ({})) : await res.text().catch(() => "");

  if (!res.ok) {
    const msg =
      (typeof data === "object" && (data.detail || data.error)) ||
      (typeof data === "string" && data.slice(0, 250)) ||
      `HTTP ${res.status}`;
    throw new Error(msg);
  }

  if (!isJson && typeof data === "string" && data.trim().startsWith("<!DOCTYPE")) {
    throw new Error(`Expected JSON but got HTML. Wrong host or missing proxy for: ${path}`);
  }

  return typeof data === "object" ? data : { ok: true, body: data };
}

function quizzesPath(orgnr) {
  return `/valuation/workspace/${encodeURIComponent(orgnr)}/quizzes/`;
}

function computeRiskScore(values) {
  if (!values) return 0;

  const {
    key_person_risk = 0,
    customer_concentration = 0,
    supplier_dependency = 0,
    contract_quality = 0,
    financial_health = 0,
    compliance_risk = 0,
  } = values;

  return (
    key_person_risk +
    customer_concentration +
    supplier_dependency +
    contract_quality +
    financial_health +
    compliance_risk
  );
}

function computeRiskFactor(values) {
  const score = computeRiskScore(values);
  if (!score) return 1.0;

  const pct = score / 30;
  const factor = 1.2 - pct * 0.5;
  return Number(factor.toFixed(2));
}

function buildDdqSummaryFromAnswers(answers) {
  let total = 0;
  let completed = 0;
  let attachments = 0;

  DDQ_SECTIONS.forEach((section) => {
    (section.items || []).forEach((q) => {
      total += 1;
      const answer = answers?.[q.qid];

      if (answer && (answer.status === "ok" || answer.status === "na")) {
        completed += 1;
      }

      const att = answer?.attachments;
      if (Array.isArray(att)) attachments += att.length;
    });
  });

  return { total, completed, attachments };
}

function riskValuesFromApi(risk) {
  if (!risk) return null;

  return {
    key_person_risk: risk.key_person_risk ?? 0,
    customer_concentration: risk.customer_concentration ?? 0,
    supplier_dependency: risk.supplier_dependency ?? 0,
    contract_quality: risk.contract_quality ?? 0,
    financial_health: risk.financial_health ?? 0,
    compliance_risk: risk.compliance_risk ?? 0,
  };
}

const MODULES = [
  {
    id: "risk",
    title: "Riskprofil",
    description:
      "Skatta nyckelrisker i verksamheten (kundkoncentration, nyckelpersoner, avtal, ekonomi, compliance) och få en indikativ multipel.",
  },
  {
    id: "owner",
    title: "Ägarfrågor",
    description:
      "Strukturerad ägarintervju: bakgrund till försäljning, ägarbild, organisation, kunder, finansiella justeringar m.m.",
  },
  {
    id: "ddq",
    title: "DD-underlag",
    description:
      "Checklista för dokument och bilagor – ladda upp material och markera status inför köparens due diligence.",
  },
];

export default function DueDiligencePortal({ orgnr: orgnrProp }) {
  const { company: companyParam } = useParams();
  const baseOrgnr = (orgnrProp || companyParam || "").trim();
  const company = companyParam || "";

  // Koncern support — shared URL-synced scope picker drives the fetch.
  const { koncernInfo, isKoncern } = useKoncernMembers(baseOrgnr);
  const { scope, setScope, activeOrgnr, isAllMode } = useKoncernScope(baseOrgnr);
  const orgnr = isAllMode ? baseOrgnr : (activeOrgnr || baseOrgnr);
  const [combinedDdq, setCombinedDdq] = useState(null);

  const [activeTab, setActiveTab] = useState("risk");

  const [bundleLoading, setBundleLoading] = useState(false);
  const [bundleSaving, setBundleSaving] = useState(false);
  const [bundleError, setBundleError] = useState("");

  const [riskApi, setRiskApi] = useState(null);
  const [ownerApi, setOwnerApi] = useState(null);
  const [ddqApi, setDdqApi] = useState(null);

  const [latestRiskValues, setLatestRiskValues] = useState(null);
  const [riskSavedAt, setRiskSavedAt] = useState(null);
  const [ownerSavedAt, setOwnerSavedAt] = useState(null);
  const [ddqSummary, setDdqSummary] = useState({
    total: 0,
    completed: 0,
    attachments: 0,
  });

  const riskScore = useMemo(() => computeRiskScore(latestRiskValues), [latestRiskValues]);
  const riskFactor = useMemo(() => computeRiskFactor(latestRiskValues), [latestRiskValues]);

  const storageKey = orgnr ? `ddp-ddq-data-v1-${orgnr}` : "ddp-ddq-data-v1";

  function applyBundle(bundle) {
    const risk = bundle?.risk || null;
    const owner = bundle?.owner || null;
    const ddq = bundle?.ddq || null;

    setRiskApi(risk);
    setOwnerApi(owner);
    setDdqApi(ddq);

    setLatestRiskValues(riskValuesFromApi(risk));
    setRiskSavedAt(risk?.updated_at || null);
    setOwnerSavedAt(owner?.updated_at || null);

    const answers = ddq?.answers || {};
    setDdqSummary(buildDdqSummaryFromAnswers(answers));
  }

  async function loadBundle({ signal } = {}) {
    if (!orgnr) return null;

    setBundleLoading(true);
    setBundleError("");

    try {
      const bundle = await apiFetch(quizzesPath(orgnr), { signal });
      applyBundle(bundle);
      return bundle;
    } catch (err) {
      if (err?.name === "AbortError") {
        return null;
      }

      setBundleError(err?.message || "Kunde inte hämta due diligence-data just nu.");
      throw err;
    } finally {
      setBundleLoading(false);
    }
  }

  async function postBundle(patch) {
    if (!orgnr) return null;

    setBundleSaving(true);
    setBundleError("");

    try {
      const bundle = await apiFetch(quizzesPath(orgnr), {
        method: "POST",
        body: patch,
      });
      applyBundle(bundle);
      return bundle;
    } catch (err) {
      if (err?.name === "AbortError") {
        return null;
      }

      setBundleError(err?.message || "Kunde inte spara just nu.");
      throw err;
    } finally {
      setBundleSaving(false);
    }
  }

  useEffect(() => {
    if (!orgnr) return;

    const ctrl = new AbortController();

    if (isAllMode) {
      // Whole-koncern view — hit the aggregated endpoint and stash per-company status.
      apiFetch(`/valuation/workspace/${encodeURIComponent(baseOrgnr)}/koncern-ddq/`, { signal: ctrl.signal })
        .then(setCombinedDdq)
        .catch((err) => {
          if (err?.name !== "AbortError") console.error("Failed to load koncern DDQ:", err);
        });
    } else {
      setCombinedDdq(null);
      loadBundle({ signal: ctrl.signal }).catch((err) => {
        if (err?.name !== "AbortError") {
          console.error("Failed to load due diligence bundle:", err);
        }
      });
    }

    return () => {
      ctrl.abort();
    };
  }, [orgnr, isAllMode, baseOrgnr]);

  function handleOpenModule(id) {
    setActiveTab(id);
  }

  const { workspace: wsLifecycle, reload: reloadLifecycle } = useWorkspaceLifecycle(company);

  return (
    <Main navItems={makeCompanyNav(company)}>
      <WorkspaceCloseBanner workspace={wsLifecycle} />
      <WorkspaceLockOverlay workspace={wsLifecycle} onActivate={reloadLifecycle}>
      <div className="ddp-root">
        <header className="ddp-header">
          <div className="ddp-header-inner">
            <h1 className="ddp-title">Due Diligence-portal</h1>
            <p className="ddp-subtitle">
              En samlad plats för riskprofil, ägarfrågor och DD-underlag inför företagsförsäljning eller investeringsrunda.
            </p>

            {bundleError && <p className="ddp-error">{bundleError}</p>}
          </div>

          {isKoncern && (
            <div style={{ padding: "8px 24px 0" }}>
              <KoncernScopePicker koncernInfo={koncernInfo} scope={scope} setScope={setScope} />
            </div>
          )}
        </header>

        {isAllMode && (
          <div
            style={{
              margin: "16px 24px",
              padding: "16px 20px",
              background: "#FEF3C7",
              border: "1px solid #FCD34D",
              borderRadius: 10,
              color: "#78350F",
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            <strong>Hela koncernen:</strong> DD-frågorna besvaras per bolag. Använd
            scope-väljaren ovan för att välja ett specifikt bolag och svara på dess
            DD. Den kombinerade vyn visar bara status per bolag.
          </div>
        )}

        <div className="ddp-top-modules">
          {MODULES.map((m) => (
            <button
              key={m.id}
              type="button"
              className={"ddp-top-module" + (activeTab === m.id ? " ddp-top-module--active" : "")}
              onClick={() => handleOpenModule(m.id)}
            >
              <h3 className="ddp-top-module-title">{m.title}</h3>
              <p className="ddp-top-module-text">{m.description}</p>
              <span className="ddp-top-module-cta">Öppna {m.title} →</span>
            </button>
          ))}
        </div>

        <div className="ddp-layout">
          <aside className="ddp-sidebar">
            <SummaryCard
              loading={bundleLoading || bundleSaving}
              riskValues={latestRiskValues}
              riskScore={riskScore}
              riskFactor={riskFactor}
              ddqSummary={ddqSummary}
              riskSavedAt={riskSavedAt}
              ownerSavedAt={ownerSavedAt}
              onOpenModule={handleOpenModule}
              wsLifecycle={wsLifecycle}
              reloadLifecycle={reloadLifecycle}
            />
          </aside>

          <main className="ddp-content">
            {activeTab === "risk" && (
              <RiskProfileSection
                orgnr={orgnr}
                loading={bundleLoading || bundleSaving}
                risk={riskApi}
                onSave={(riskPatch) => postBundle({ risk: riskPatch })}
                onSavedLocal={(data) => {
                  setRiskSavedAt(data?.updated_at || new Date().toISOString());
                  setLatestRiskValues(riskValuesFromApi(data));
                }}
              />
            )}

            {activeTab === "owner" && (
              <OwnerQuestionnaireSection
                orgnr={orgnr}
                loading={bundleLoading || bundleSaving}
                owner={ownerApi}
                onSave={(ownerPatch) => postBundle({ owner: ownerPatch })}
                onSavedLocal={(data) => {
                  setOwnerSavedAt(data?.updated_at || new Date().toISOString());
                }}
              />
            )}

            {activeTab === "ddq" && (
              <DueDiligenceSection
                orgnr={orgnr}
                storageKey={storageKey}
                ddq={ddqApi}
                onSaveDdq={(ddqPatch) => postBundle({ ddq: ddqPatch })}
                onSummaryChange={setDdqSummary}
              />
            )}
          </main>
        </div>
      </div>
      </WorkspaceLockOverlay>
    </Main>
  );
}

function SummaryCard({
  loading,
  riskValues,
  riskScore,
  riskFactor,
  ddqSummary,
  riskSavedAt,
  ownerSavedAt,
  onOpenModule,
  wsLifecycle,
  reloadLifecycle,
}) {
  // Vissa när hela DD-checklistan är klar — annars är "Avsluta projekt"
  // för tidigt erbjudet. Brokern behöver också can_manage_lifecycle.
  const allDdqDone =
    ddqSummary &&
    ddqSummary.total > 0 &&
    ddqSummary.completed === ddqSummary.total;
  const showClose = allDdqDone && wsLifecycle?.can_manage_lifecycle;
  return (
    <section className="ddp-summary-section">
      <div className="ddp-card ddp-card-summary">
        <div className="ddp-card-block">
          <div className="ddp-card-head-inline">
            <div className="ddp-qid">A</div>
            <div className="ddp-qtext">Riskprofil & multipel</div>
          </div>

          <div className="ddp-card-body">
            {loading ? (
              <p className="ddp-muted">Hämtar/sparar…</p>
            ) : (
              <>
                <p className="ddp-summary-line-label">Sammanfattning</p>
                <p className="ddp-summary-text">
                  Total riskscore: <strong>{riskScore}</strong> av 30.
                  <br />
                  Föreslagen värderingsfaktor: <strong>{riskFactor.toFixed(2)}</strong> (ca 0,70–1,20).
                </p>

                {!riskValues && (
                  <p className="ddp-error">
                    Ingen riskprofil inhämtad ännu. Gå till{" "}
                    <button type="button" className="ddp-link-button" onClick={() => onOpenModule("risk")}>
                      Riskprofil
                    </button>{" "}
                    för att fylla i.
                  </p>
                )}

                <div className="ddp-summary-meta">
                  <p className="ddp-summary-meta-title">Senast uppdaterad</p>
                  <p>
                    Riskprofil: <strong>{riskSavedAt ? new Date(riskSavedAt).toLocaleString("sv-SE") : "–"}</strong>
                    <br />
                    Ägarfrågor: <strong>{ownerSavedAt ? new Date(ownerSavedAt).toLocaleString("sv-SE") : "–"}</strong>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        <hr className="ddp-card-separator" />

        <div className="ddp-card-block">
          <div className="ddp-card-head-inline">
            <div className="ddp-qid">B</div>
            <div className="ddp-qtext">Due Diligence-status</div>
          </div>

          <div className="ddp-card-body">
            {loading ? (
              <p className="ddp-muted">Hämtar/sparar…</p>
            ) : (
              <>
                <p className="ddp-summary-line-label">Frågelista</p>
                <p className="ddp-summary-text">
                  Färdiga frågor:{" "}
                  <strong>
                    {ddqSummary.completed} / {ddqSummary.total}
                  </strong>
                  <br />
                  Antal bilagor: <strong>{ddqSummary.attachments}</strong>
                </p>

                <p className="ddp-muted">
                  För att justera status och ladda upp fler dokument – gå till{" "}
                  <button type="button" className="ddp-link-button" onClick={() => onOpenModule("ddq")}>
                    DD-underlag
                  </button>
                  .
                </p>

                {/* Avsluta-projekt-CTA syns endast när hela DD-checklistan
                    är 100% klar och brokern har lifecycle-rätt. hideBilling
                    → dialogen säger inget om Stripe, bara att köpare och
                    säljare sparkas ut. */}
                {showClose && (
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #E7E5E4" }}>
                    <p style={{ fontSize: 12, color: "#374151", margin: "0 0 8px", fontWeight: 600 }}>
                      Hela DD-checklistan är klar
                    </p>
                    <CloseWorkspaceButton
                      workspace={wsLifecycle}
                      onClosed={reloadLifecycle}
                      label="Avsluta projekt"
                      hideBilling
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function RiskProfileSection({ orgnr, loading, risk, onSave, onSavedLocal }) {
  return (
    <section>
      <h2>Riskprofil</h2>
      <p className="ddp-section-intro">
        Här skattar vi centrala riskfaktorer i verksamheten. Resultatet summeras i A-rutan till vänster.
      </p>

      <RiskProfileQuestionnaireForm
        orgnr={orgnr}
        loading={loading}
        initialData={risk}
        onSubmit={onSave}
        onSaved={(data) => onSavedLocal(data)}
      />
    </section>
  );
}

function OwnerQuestionnaireSection({ orgnr, loading, owner, onSave, onSavedLocal }) {
  return (
    <section>
      <h2>Ägarfrågor</h2>
      <p className="ddp-section-intro">
        Ägarintervju med fokus på bakgrund till affären, ägarbild, organisation, kunder och finansiella justeringar.
      </p>

      <OwnerQuestionnaireForm
        orgnr={orgnr}
        loading={loading}
        initialData={owner}
        onSubmit={onSave}
        onSaved={(data) => onSavedLocal(data)}
      />
    </section>
  );
}

function DueDiligenceSection({ orgnr, storageKey, ddq, onSaveDdq, onSummaryChange }) {
  return (
    <section>
      <h2>DD-underlag</h2>
      <p className="ddp-section-intro">
        Följ checklistan nedan, markera status för varje punkt och ladda upp relevanta dokument. Den övergripande statusen syns i B-rutan till vänster.
      </p>

      <DueDiligenceChecklist
        company={orgnr}
        storageKey={storageKey}
        initialData={ddq}
        onSubmit={onSaveDdq}
        onSummaryChange={onSummaryChange}
      />
    </section>
  );
}